import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import * as React from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@volca/ui/components/ui/input-group";
import { cn } from "@volca/ui/lib/utils";

export const SEARCH_INPUT_VARIANT = {
  field: "field",
  bare: "bare",
} as const;

export type SearchInputVariant =
  (typeof SEARCH_INPUT_VARIANT)[keyof typeof SEARCH_INPUT_VARIANT];

export interface SearchInputProps
  extends Omit<React.ComponentProps<"input">, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  className?: string;
  placeholder?: string;
  variant?: SearchInputVariant;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onClear,
      className,
      placeholder = "Search",
      variant = SEARCH_INPUT_VARIANT.field,
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    const handleClear = () => {
      onChange("");
      onClear?.();
    };

    return (
      <InputGroup
        className={cn(
          "h-9 sm:h-8",
          variant === SEARCH_INPUT_VARIANT.bare ? "w-full" : "w-75",
          className,
        )}
      >
        <InputGroupAddon align="inline-start">
          <MagnifyingGlassIcon />
        </InputGroupAddon>
        <InputGroupInput
          ref={ref}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          aria-label={props["aria-labelledby"] ? undefined : placeholder}
          {...props}
        />
        {value && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              size="icon-xs"
              onClick={handleClear}
              aria-label="Clear search"
            >
              <XIcon />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>
    );
  },
);

SearchInput.displayName = "SearchInput";
